import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Clock, Sparkle, CheckCircle, Info, X, WarningCircle } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Rough ETA map based on Magic Hour Talking Photo benchmarks
// (2–3 min per 5 sec of output, plus ~60s overhead for script+voice+uploads)
const LENGTH_ETA_SECONDS = {
  "15sec": 6 * 60,        // ~6 min for a ~15s clip
  "30sec": 14 * 60,       // ~14 min
  "45-60sec": 28 * 60,    // ~28 min
  "60sec": 28 * 60,
  "1-2min": 65 * 60,      // ~65 min (close to MH p99 for ~90s)
  "2-3min": 150 * 60,     // ~2.5 hrs (Magic Hour's new 3-min cap)
};

const STEP_LABELS = {
  script: "Writing script with Claude",
  scripting: "Writing script with Claude",
  voice: "Generating voice clone audio",
  video: "Magic Hour animating your AI Twin",
  sync: "Syncing lips to audio",
  broll: "Generating B-roll cutaways",
  captions: "Adding captions",
  captioning: "Adding captions",
  done: "Complete",
};

export default function RenderBanner({ jobs, currentStep, onJobCancelled }) {
  const [now, setNow] = useState(Date.now());
  const [cancelling, setCancelling] = useState(false);

  // Recompute every second for live countdown
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // Find the most recent active/processing job
  const activeJob = useMemo(() => {
    if (!jobs || jobs.length === 0) return null;
    const active = jobs.find((j) =>
      ["processing", "scripting", "syncing", "captioning", "broll", "video"].includes(j.status) ||
      (j.status === "processing" && j.current_step !== "done")
    );
    return active || null;
  }, [jobs]);

  if (!activeJob) return null;

  const createdAt = new Date(activeJob.created_at).getTime();
  const elapsedMs = Math.max(0, now - createdAt);
  const elapsedSec = Math.floor(elapsedMs / 1000);
  const etaSec = LENGTH_ETA_SECONDS[activeJob.length] || 14 * 60;
  const progressPct = Math.min(95, Math.floor((elapsedSec / etaSec) * 100));
  const remainingSec = Math.max(0, etaSec - elapsedSec);

  const fmt = (s) => {
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const rem = s % 60;
    return rem === 0 ? `${m}m` : `${m}m ${rem}s`;
  };

  const stepLabel = STEP_LABELS[currentStep] || STEP_LABELS[activeJob.current_step] || "Processing";
  const topic = (activeJob.topic || "your video").slice(0, 60);
  const overEta = elapsedSec > etaSec;
  // After 2x ETA we stop pretending it'll wrap up "any minute" and warn the
  // user that Magic Hour is likely stuck on their side. This also reveals the
  // Cancel button.
  const isStuck = elapsedSec > etaSec * 2;

  const handleCancel = async () => {
    if (!activeJob?.id || cancelling) return;
    if (!window.confirm("Cancel this render? The credit will not be refunded, but your UI unblocks immediately so you can start a fresh job.")) return;
    setCancelling(true);
    try {
      await axios.post(`${API}/auto-create/jobs/${activeJob.id}/cancel`);
      if (onJobCancelled) onJobCancelled(activeJob.id);
    } catch (e) {
      alert("Cancel failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div
      data-testid="render-banner"
      className={`rounded-2xl border p-5 shadow-sm ${
        isStuck
          ? "border-amber-300 bg-gradient-to-br from-amber-50 via-white to-amber-50/50"
          : "border-[#E91E8C]/30 bg-gradient-to-br from-[#E91E8C]/5 via-white to-[#ff6ec7]/5"
      }`}
    >
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 mt-0.5">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            isStuck ? "bg-amber-100" : "bg-[#E91E8C]/10"
          }`}>
            {isStuck ? (
              <WarningCircle size={20} weight="duotone" className="text-amber-600" />
            ) : (
              <Sparkle size={20} weight="duotone" className="text-[#E91E8C] animate-pulse" />
            )}
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-4 mb-2">
            <div>
              <h3 className="text-sm font-bold text-zinc-900">
                {isStuck ? "Render is taking unusually long" : "Rendering"} <span className={isStuck ? "text-amber-700" : "text-[#E91E8C]"}>"{topic}"</span>
              </h3>
              <p className="text-xs text-zinc-600 mt-0.5" data-testid="render-banner-step">
                {stepLabel}...
              </p>
            </div>
            <div className="flex items-center gap-2 whitespace-nowrap">
              <div className="flex items-center gap-1.5 text-xs text-zinc-500">
                <Clock size={14} />
                <span data-testid="render-banner-elapsed" className="tabular-nums">{fmt(elapsedSec)} elapsed</span>
              </div>
              {/* Cancel is ALWAYS available during a render — users explicitly
                  asked to be able to bail at any time, not just after the ETA
                  has expired. */}
              <button
                onClick={handleCancel}
                disabled={cancelling}
                className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-red-50 border border-red-200 text-red-700 hover:bg-red-100 transition-all disabled:opacity-50"
                data-testid="render-banner-cancel"
              >
                <X size={12} weight="bold" />
                {cancelling ? "Cancelling..." : "Cancel"}
              </button>
            </div>
          </div>

          {/* Progress bar */}
          <div className="h-2 rounded-full bg-zinc-100 overflow-hidden mb-2">
            <div
              data-testid="render-banner-progress"
              className={`h-full rounded-full transition-all duration-1000 ${
                isStuck
                  ? "bg-gradient-to-r from-amber-400 to-amber-500"
                  : "bg-gradient-to-r from-[#E91E8C] to-[#ff6ec7] animate-pulse"
              }`}
              style={{ width: `${progressPct}%` }}
            />
          </div>

          <div className="flex items-center justify-between text-[11px] text-zinc-500">
            <span data-testid="render-banner-remaining" className="tabular-nums">
              {isStuck
                ? "Magic Hour may be queued or stuck. Cancel and retry — switch to Lip Sync mode for 5–8x faster renders."
                : overEta
                  ? "Wrapping up any minute now..."
                  : `Estimated time remaining: ~${fmt(remainingSec)}`}
            </span>
            <span className={`tabular-nums font-semibold ${isStuck ? "text-amber-700" : "text-[#E91E8C]"}`}>{progressPct}%</span>
          </div>

          {/* Soft reassurance — close tab message (only when NOT stuck) */}
          {!isStuck && (
            <div className="mt-3 pt-3 border-t border-zinc-100 flex items-start gap-2">
              <Info size={14} className="text-zinc-400 flex-shrink-0 mt-0.5" />
              <p className="text-[11px] text-zinc-500 leading-relaxed">
                You can <strong className="text-zinc-700">close this tab or navigate away</strong> — we'll send you a notification
                (top-right bell <CheckCircle size={11} weight="fill" className="inline text-emerald-500 mx-0.5" />) the moment your video is ready.
              </p>
            </div>
          )}

          {isStuck && (
            <div className="mt-3 pt-3 border-t border-amber-200 flex items-start gap-2">
              <WarningCircle size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-[11px] text-amber-800 leading-relaxed">
                Renders this slow are almost always Magic Hour queue congestion. <strong>Cancel above</strong> and start a fresh job — if it happens again, switch to <strong>Lip Sync mode</strong> (Talking Photo can take up to 30 min/chunk on busy days).
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
